import { Menu, Bell, Search, Plus, Filter, LayoutGrid, List, Calendar, DollarSign, FileText, Settings, UserCircle, LogOut, Keyboard  } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Outlet, useParams, useLocation, Form, useLoaderData, useRouteLoaderData } from 'react-router'
import { cn } from '#app/utils/misc'
import { useOptionalUser } from '#app/utils/user'
import { AppSidebar } from '../app-sidebar'
import { useIsMobile } from '../design-system/ResponsiveHelpers'
import { Breadcrumbs, MobileBreadcrumbs } from '../navigation/Breadcrumbs'
import { MobileNavigation } from '../mobile/MobileNavigation'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandShortcut } from '../ui/command'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuShortcut,
} from '../ui/dropdown-menu'
import { Input } from '../ui/input'
import { SidebarProvider, SidebarInset } from '../ui/sidebar'

interface EnhancedProjectLayoutProps {
  children?: React.ReactNode
}

export function EnhancedProjectLayout({ children }: EnhancedProjectLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [searchOpen, setSearchOpen] = useState(false)
  const [commandOpen, setCommandOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const isMobile = useIsMobile()
  const params = useParams()
  const location = useLocation()
  const user = useOptionalUser()
  
  // Try to get project data from current route loader data or dashboard route
  const loaderData = useLoaderData() as any
  const dashboardData = useRouteLoaderData('routes/dashboard') as any
  const defaultProjectId = params.projectId || loaderData?.project?.id || dashboardData?.project?.id

  // Get current page title based on route
  const getPageTitle = () => {
    const path = location.pathname
    if (path.includes('orientation')) return 'Orientation Phase'
    if (path.includes('planning')) return 'Planning Phase'
    if (path.includes('building')) return 'Building Phase'
    if (path.includes('finishing')) return 'Finishing Phase'
    if (path.includes('budget')) return 'Budget Management'
    if (path.includes('timeline')) return 'Project Timeline'
    if (path.includes('documents')) return 'Documents'
    if (path.includes('team')) return 'Team Management'
    if (path.includes('dashboard')) return 'Dashboard'
    if (path.includes('settings')) return 'Settings'
    if (path.includes('profile')) return 'Profile'
    return 'Woonplan'
  }

  // Keyboard shortcut for command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCommandOpen(true)
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Quick actions for command palette
  const quickActions = [
    { icon: Plus, label: 'New Project', shortcut: '⌘N', action: () => {} },
    { icon: FileText, label: 'Upload Document', shortcut: '⌘U', action: () => {} },
    { icon: Calendar, label: 'View Timeline', shortcut: '⌘T', action: () => {} },
    { icon: DollarSign, label: 'Check Budget', shortcut: '⌘B', action: () => {} },
  ]

  // Notification items
  const notifications = [
    {
      id: '1',
      title: 'Task completed',
      description: 'Kitchen demolition has been marked as complete',
      time: '2 hours ago',
      unread: true,
      type: 'success' as const,
    },
    {
      id: '2',
      title: 'New comment',
      description: 'Sarah commented on the bathroom design',
      time: '5 hours ago',
      unread: true,
      type: 'info' as const,
    },
    {
      id: '3',
      title: 'Budget alert',
      description: 'You\'re approaching your kitchen budget limit',
      time: '1 day ago',
      unread: false,
      type: 'warning' as const,
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Command Palette */}
      <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
        <CommandInput placeholder="Type a command or search..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Quick Actions">
            {quickActions.map((action) => (
              <CommandItem
                key={action.label}
                onSelect={() => {
                  action.action()
                  setCommandOpen(false)
                }}
              >
                <action.icon className="mr-2 h-4 w-4" />
                <span>{action.label}</span>
                <CommandShortcut>{action.shortcut}</CommandShortcut>
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandGroup heading="Navigation">
            <CommandItem>Dashboard</CommandItem>
            <CommandItem>Projects</CommandItem>
            <CommandItem>Settings</CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      {/* Desktop Layout */}
      <SidebarProvider defaultOpen={sidebarOpen} onOpenChange={setSidebarOpen}>
      <div className="hidden lg:flex h-screen w-full">
        {/* Enhanced Sidebar */}
        <AppSidebar defaultProjectId={defaultProjectId} />

        {/* Main Content */}
        <SidebarInset className="flex-1 flex flex-col overflow-hidden">
          {/* Top Header */}
          <header className="bg-white border-b border-gray-200">
            <div className="px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="lg:flex"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                  
                  {/* Breadcrumbs */}
                  <Breadcrumbs className="hidden md:flex" />
                </div>

                <div className="flex items-center gap-3">
                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <Button
                      variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setViewMode('grid')}
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setViewMode('list')}
                    >
                      <List className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Command Palette Trigger */}
                  <Button
                    variant="outline"
                    className="hidden md:flex items-center gap-2 text-gray-600"
                    onClick={() => setCommandOpen(true)}
                  >
                    <Search className="h-4 w-4" />
                    <span className="text-sm">Search...</span>
                    <kbd className="ml-8 pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-gray-100 px-1.5 font-mono text-[10px] font-medium opacity-100">
                      <span className="text-xs">⌘</span>K
                    </kbd>
                  </Button>

                  {/* Mobile Search */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    onClick={() => setCommandOpen(true)}
                  >
                    <Search className="h-5 w-5" />
                  </Button>

                  {/* Notifications */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="relative">
                        <Bell className="h-5 w-5" />
                        {notifications.filter(n => n.unread).length > 0 && (
                          <Badge
                            variant="destructive"
                            className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
                          >
                            {notifications.filter(n => n.unread).length}
                          </Badge>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-96">
                      <DropdownMenuLabel className="flex items-center justify-between">
                        <span>Notifications</span>
                        <Button variant="ghost" size="sm" className="h-auto p-0 text-xs">
                          Mark all as read
                        </Button>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.map((notification) => (
                          <NotificationItem key={notification.id} {...notification} />
                        ))}
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="justify-center cursor-pointer">
                        View all notifications
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* User Menu */}
                  {user && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={user.image?.objectKey ? `/resources/images/${user.image.objectKey}` : undefined} />
                            <AvatarFallback>
                              {user.name?.charAt(0) || user.username.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel>
                          <div className="flex flex-col space-y-1">
                            <p className="text-sm font-medium">{user.name || user.username}</p>
                            <p className="text-xs text-gray-500">{user.username}</p>
                          </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                          <DropdownMenuItem>
                            <UserCircle className="mr-2 h-4 w-4" />
                            Profile
                            <DropdownMenuShortcut>⌘P</DropdownMenuShortcut>
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Settings className="mr-2 h-4 w-4" />
                            Settings
                            <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Keyboard className="mr-2 h-4 w-4" />
                            Keyboard shortcuts
                            <DropdownMenuShortcut>⌘K</DropdownMenuShortcut>
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />
                        <Form method="post" action="/logout">
                          <DropdownMenuItem asChild>
                            <button type="submit" className="flex w-full cursor-pointer items-center text-red-600">
                              <LogOut className="mr-2 h-4 w-4" />
                              Sign out
                              <DropdownMenuShortcut>⌘Q</DropdownMenuShortcut>
                            </button>
                          </DropdownMenuItem>
                        </Form>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            </div>
            
            {/* Page Title Bar */}
            <div className="px-6 py-3 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{getPageTitle()}</h1>
                  {params.projectId && (
                    <p className="text-sm text-gray-500 mt-0.5">Amsterdam Loft Renovation</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">
                    <Filter className="mr-2 h-4 w-4" />
                    Filter
                  </Button>
                  <Button size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Add New
                  </Button>
                </div>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-y-auto bg-gray-50">
            <div className="p-6">
              {children || <Outlet />}
            </div>
          </main>
        </SidebarInset>
      </div>
      </SidebarProvider>

      {/* Mobile Layout */}
      <div className="lg:hidden">
        {/* Mobile Header */}
        <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <MobileBreadcrumbs />
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setCommandOpen(true)}>
                <Search className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                {notifications.filter(n => n.unread).length > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-xs"
                  >
                    {notifications.filter(n => n.unread).length}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
        </header>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="fixed left-0 top-0 z-50 h-full w-72 bg-white">
              <SidebarProvider defaultOpen={true}>
                <AppSidebar defaultProjectId={defaultProjectId} />
              </SidebarProvider>
            </div>
          </>
        )}

        {/* Mobile Content */}
        <main className="pt-14 pb-20 px-4">
          {children || <Outlet />}
        </main>

        {/* Mobile Bottom Navigation */}
        <MobileNavigation />
      </div>
    </div>
  )
}

function NotificationItem({
  title,
  description,
  time,
  unread = false,
  type = 'info',
}: {
  title: string
  description: string
  time: string
  unread?: boolean
  type?: 'success' | 'info' | 'warning' | 'error'
}) {
  const typeStyles = {
    success: 'bg-green-50 text-green-600',
    info: 'bg-blue-50 text-blue-600',
    warning: 'bg-yellow-50 text-yellow-600',
    error: 'bg-red-50 text-red-600',
  }

  return (
    <DropdownMenuItem className="p-4 focus:bg-gray-50 cursor-pointer">
      <div className="flex items-start gap-3 w-full">
        <div className={cn(
          'w-2 h-2 rounded-full mt-1.5 flex-shrink-0',
          unread ? 'bg-blue-500' : 'bg-gray-300'
        )} />
        <div className="flex-1 space-y-1">
          <p className="text-sm font-medium text-gray-900">{title}</p>
          <p className="text-xs text-gray-600 line-clamp-2">{description}</p>
          <p className="text-xs text-gray-400">{time}</p>
        </div>
        {type && (
          <div className={cn(
            'px-2 py-1 rounded-full text-xs font-medium',
            typeStyles[type]
          )}>
            {type}
          </div>
        )}
      </div>
    </DropdownMenuItem>
  )
}